/**
 * The founder-only admin surfaces: the cockpit page `/mnb/apka` and the Android
 * shell (`pl.prawmi.admin`) that wraps it in a WebView.
 *
 * These differ from the rest of prawmi.pl in ways several unrelated components
 * have to honour, so the test lives here once instead of being re-derived:
 * - the language is always Polish, whatever the phone's locale says
 *   (see i18n.ts) - the app has exactly two users and both are Polish;
 * - no cookie banner (see app/components/CookieConsentBannerNext.tsx): `/mnb`
 *   loads no analytics at all (isInternalPath in utils/ga.ts), so there is no
 *   non-essential cookie to consent to and the banner only covers the cockpit.
 *
 * The shell is recognised by the marker it appends to the WebView user agent
 * (MainActivity.configureWebView: "... PrawMiAdmin/<versionName>").
 */

/** User-agent marker appended by the Android shell. Keep in sync with MainActivity. */
export const ADMIN_SHELL_UA_MARKER = 'PrawMiAdmin/';

/** Cockpit route; deep links from notifications are nested under it. */
export const ADMIN_COCKPIT_PATH = '/mnb/apka';

/** True for the cockpit page itself and anything nested below it. */
export const isAdminCockpitPath = (pathname: string | null | undefined): boolean => {
  if (!pathname) return false;
  return (
    pathname === ADMIN_COCKPIT_PATH || pathname.startsWith(`${ADMIN_COCKPIT_PATH}/`)
  );
};

/** True when the page is running inside the Android admin shell. */
export const isAdminShellUserAgent = (userAgent?: string | null): boolean => {
  const value =
    userAgent ?? (typeof navigator === 'undefined' ? null : navigator.userAgent);
  return !!value && value.includes(ADMIN_SHELL_UA_MARKER);
};

/**
 * Marker the shell appends: `PrawMiAdmin/<version>` or
 * `PrawMiAdmin/<version>+<yyyyMMddHHmm>` once the build carries a date.
 *
 * The `+<stamp>` half is optional on purpose: an older shell (a downgrade is
 * one tap in Obtainium) sends the bare version and simply reports no date.
 */
// The version class deliberately excludes `+`, so the greedy first group
// stops exactly where the optional stamp begins and cannot swallow it.
const SHELL_UA_PATTERN = /PrawMiAdmin\/([0-9A-Za-z.\-_]+)(?:\+(\d{12}))?/;

/** Compact stamp length: yyyy(4) MM(2) dd(2) HH(2) mm(2). */
const STAMP_LENGTH = 12;

/**
 * `202609101520` -> `10.09.'26, 15:20`. Null for anything else.
 *
 * The stamp is already local time in Europe/Warsaw (AppVersion.kt), so it is
 * split rather than parsed: handing these digits to `new Date()` would have the
 * browser reinterpret them in the phone's own zone and shift the hour.
 */
export const formatShellBuildStamp = (stamp?: string | null): string | null => {
  if (!stamp || stamp.length !== STAMP_LENGTH || !/^\d+$/.test(stamp)) return null;
  const year = stamp.slice(2, 4);
  const month = stamp.slice(4, 6);
  const day = stamp.slice(6, 8);
  const hour = stamp.slice(8, 10);
  const minute = stamp.slice(10, 12);
  return `${day}.${month}.'${year}, ${hour}:${minute}`;
};

export interface AdminShellBuild {
  /** Version name of the installed APK, e.g. `0.1.2`. */
  version: string;
  /** When that build was cut, e.g. `10.09.'26, 15:20`; null on an older shell. */
  builtAt: string | null;
}

/**
 * Which build of the Android shell wraps this page, or null in a browser.
 *
 * Read from the user-agent marker rather than from the `window.PrawMiAdmin`
 * bridge: the marker is present on the very first paint and on every request,
 * while the bridge is injected per WebView and is absent from a server render.
 * "Czy update wszedl?" is the first question after every release, and a version
 * that only appears once the bridge lands answers it a beat too late.
 */
export const adminShellBuild = (userAgent?: string | null): AdminShellBuild | null => {
  const value =
    userAgent ?? (typeof navigator === 'undefined' ? null : navigator.userAgent);
  if (!value) return null;
  const match = value.match(SHELL_UA_PATTERN);
  if (!match) return null;
  return { version: match[1], builtAt: formatShellBuildStamp(match[2]) };
};

/** Version of the Android shell wrapping this page, or null in a browser. */
export const adminShellVersion = (userAgent?: string | null): string | null =>
  adminShellBuild(userAgent)?.version ?? null;

/**
 * True on any founder-only admin surface: the cockpit route in a browser, or
 * any page opened inside the Android shell.
 */
export const isAdminSurface = (pathname?: string | null): boolean => {
  if (typeof window === 'undefined') return false;
  const path = pathname ?? window.location.pathname;
  return isAdminCockpitPath(path) || isAdminShellUserAgent();
};
